// @ts-strict

import { formatDate } from '@angular/common';
import { Injectable, OnDestroy } from '@angular/core';
import { StatisticsSummaryService } from '@services/statistics/statistics-summary.service';
import { NgbCalendar, NgbDate } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { StatsRequestParams } from '@pages/statistics/shared/interfaces/statistics';

@Injectable({
  providedIn: 'root'
})
export class StatisticsService implements OnDestroy {
  private compareRangeTooWideSubject$ = new BehaviorSubject<{ isRangeTooWide: boolean; maxDaysAllowed: number }>({ isRangeTooWide: false, maxDaysAllowed: 0 });

  private statisticsRequestParamsSubject$ = new BehaviorSubject<StatsRequestParams>({ ...this.getInitialRequestDate() });

  public compareRangeTooWide$ = this.compareRangeTooWideSubject$.asObservable();

  private firstStatisticsDatePickerDates?: { date_from: string; date_to: string };
  private compareMode = false;

  private destroy$ = new Subject<void>();

  constructor(
    private readonly statistics: StatisticsSummaryService,
    private readonly calendar: NgbCalendar
  ) {
    this.watchFirstStatisticsDatePicker();
    this.watchForCompareStatisticsDatePicker();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public prepareCompareDates = (dateFrom: NgbDate, dateTo: NgbDate): { date_from: string; date_to: string } => {
    return {
      date_from: `${dateFrom.year}-${dateFrom.month}-${dateFrom.day}`,
      date_to: `${dateTo.year}-${dateTo.month}-${dateTo.day}`
    };
  };

  public checkIfCompareRangeTooWide = (dateFrom: NgbDate, dateTo: NgbDate, picker: 'compare' | 'general', selectedRange: string) => {
    const { date_from, date_to } = this.prepareCompareDates(dateFrom, dateTo);

    const compareDatesDifference = this.getNumberOfDaysBetweenTwoPeriods(date_from, date_to);

    let datesDifference: number;

    if (picker === 'compare' && this.firstStatisticsDatePickerDates) {
      datesDifference = this.getNumberOfDaysBetweenTwoPeriods(this.firstStatisticsDatePickerDates.date_from, this.firstStatisticsDatePickerDates.date_to);
    }

    if (picker === 'general') {
      datesDifference = compareDatesDifference;

      if (this.compareMode) {
        this.statistics.resetCompareMode();
      }
    }

    const isRangeTooWide = compareDatesDifference !== datesDifference!;

    this.compareRangeTooWideSubject$.next({ isRangeTooWide: isRangeTooWide, maxDaysAllowed: datesDifference! });
  };

  public getNumberOfDaysBetweenTwoPeriods = (date_from: string, date_to: string): number => {
    return moment(date_to, 'YYYY-MM-DD').diff(moment(date_from, 'YYYY-MM-DD'), 'days') + 1;
  };

  public setStatisticsRequestParams = (requestParams: StatsRequestParams) => {
    this.statisticsRequestParamsSubject$.next(requestParams);
  };

  public resetStatisticsRequestParams = () => {
    this.statisticsRequestParamsSubject$.next({ ...this.getInitialRequestDate() });
  };

  public watchStatisticsRequestParams$ = (): Observable<StatsRequestParams> => {
    return this.statisticsRequestParamsSubject$.asObservable();
  };

  private watchFirstStatisticsDatePicker = () => {
    this.statistics.dateRange
      .pipe(
        tap(dateRange => {
          this.firstStatisticsDatePickerDates = this.prepareCompareDates(dateRange.dateFrom, dateRange.dateTo);
        }, takeUntil(this.destroy$))
      )
      .subscribe();
  };

  private watchForCompareStatisticsDatePicker = () => {
    this.statistics.compareMode
      .pipe(
        tap(({ compareMode }) => {
          this.compareMode = compareMode;
        }, takeUntil(this.destroy$))
      )
      .subscribe();
  };

  public getInitialRequestDate() {
    const format = 'YYYY-MM-dd';
    const locale = 'en';

    const dateFrom = this.calendar.getPrev(this.calendar.getToday(), 'd', 30);
    const dateTo = this.calendar.getToday();

    return {
      date_from: this.stringifyNgbDate(dateFrom),
      date_to: this.stringifyNgbDate(dateTo)
    };
  }

  public stringifyNgbDate(ngbDate: NgbDate): string {
    // return ngbDate.year + '-' + ngbDate.month + '-' + ngbDate.day;
    const month = ngbDate.month.toString().padStart(2, '0');
    const day = ngbDate.day.toString().padStart(2, '0');

    return `${ngbDate.year}-${month}-${day}`;
  }
}
